
import { Component, Input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { TrainingProgress } from '@vendasta/embeddings';
import { isTrainingFinished } from '../../ai-knowledge-util';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { AiLoadingSpinnerComponent } from '../ai-loading-spinner/ai-loading-spinner.component';
import { AiKnowledgeI18nModule } from '../../assets/i18n/ai-knowledge-i18n.module';
import { TrainingProgressConfig, shouldShowTrainingProgress } from './training-progress.utils';

@Component({
  selector: 'ai-knowledge-training-progress',

  imports: [TranslateModule, AiKnowledgeI18nModule, GalaxyEmptyStateModule, AiLoadingSpinnerComponent],
  templateUrl: './training-progress.component.html',
  styleUrls: ['./training-progress.component.scss'],
})
export class TrainingProgressComponent {
  @Input() config: TrainingProgressConfig = {
    trainingInfo: null,
    delayProgressScreenClosure: false,
    trainingEnabledPageCount: 0,
  };
  @Input() shouldShowDone = false;

  shouldShowTrainingProgress(): boolean {
    return shouldShowTrainingProgress(this.config, this.shouldShowDone);
  }

  showDone(): boolean {
    return this.shouldShowDone && !!this.config.trainingInfo && isTrainingFinished(this.config.trainingInfo);
  }

  getTrainingProgressTitle(): string {
    // Check if we're in completion state first
    if (this.showDone()) {
      return 'EDIT_KNOWLEDGE.TRAINING.TRAINING_DONE';
    }

    if (this.shouldShowProgressCount()) {
      // Use different translation for website sources (page count) vs others (percentage)
      return this.config.trainingEnabledPageCount && this.config.trainingEnabledPageCount > 0
        ? 'EDIT_KNOWLEDGE.TRAINING.TRAINING_IN_PROGRESS_PAGES'
        : 'EDIT_KNOWLEDGE.TRAINING.TRAINING_IN_PROGRESS_PERCENT';
    } else {
      return 'EDIT_KNOWLEDGE.TRAINING.TRAINING_QUEUED';
    }
  }

  shouldShowProgressCount(): boolean {
    if (!this.config.trainingInfo || this.showDone()) {
      return false;
    }

    // Show progress count during training or when done with delay
    return (
      this.config.trainingInfo.progress === TrainingProgress.TRAINING_IN_PROGRESS ||
      (!!this.config.delayProgressScreenClosure && isTrainingFinished(this.config.trainingInfo))
    );
  }

  getProgressDisplay(): string {
    if (this.config.trainingEnabledPageCount && this.config.trainingEnabledPageCount > 0) {
      return this.getPageProgressCount().toString();
    } else {
      return `${this.getPercentage()}%`;
    }
  }

  private getPercentage(): number {
    if (!this.config.trainingInfo) {
      return 0;
    }

    const totalUnits = this.config.trainingInfo.totalUnits || 0;
    const completedUnits = this.config.trainingInfo.completedUnits || 0;

    if (totalUnits === 0) {
      return 0;
    }

    const percentage = (completedUnits / totalUnits) * 100;
    return Math.round(percentage);
  }

  private getPageProgressCount(): number {
    if (!this.config.trainingInfo || !this.config.trainingEnabledPageCount) {
      return 0;
    }

    if (this.config.trainingInfo.totalUnits > 0 && this.config.trainingInfo.completedUnits > 0) {
      return Math.round(
        (this.config.trainingInfo.completedUnits / this.config.trainingInfo.totalUnits) *
        this.config.trainingEnabledPageCount,
      );
    } else {
      return 0;
    }
  }

  protected readonly TrainingProgress = TrainingProgress;
  protected readonly Math = Math;
}
